
import React, { useState } from 'react';
import MainLayout from '@/components/layouts/MainLayout';
import PageHeader from '@/components/common/PageHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CreditCard, AlertTriangle, Info } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { usePaymentMethods } from '@/hooks/usePaymentMethods';
import { useAuthor } from '@/hooks/useAuthors';
import { formatCurrency, formatPaymentMethod } from '@/utils/formatters';
import { Skeleton } from '@/components/ui/skeleton';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';

const PaymentPreferences = () => {
  const { user } = useAuth();
  const userId = user?.id || '';
  const [activeTab, setActiveTab] = useState('payment-methods');
  
  const { 
    data: paymentMethodsData, 
    isLoading: isLoadingPayments
  } = usePaymentMethods(userId);
  
  const { data: authorData, isLoading: isLoadingAuthor } = useAuthor(userId);
  
  const defaultMethod = paymentMethodsData?.find(method => method.is_default);
  const otherMethods = paymentMethodsData?.filter(method => !method.is_default) || [];
  
  const isLoading = isLoadingPayments || isLoadingAuthor;

  const renderPaymentMethodDetails = (method: any) => {
    const paymentDetails = method.payment_details?.[0];
    if (!paymentDetails) {
      return <p className="text-muted-foreground">No payment details available</p>;
    }

    return (
      <div className="space-y-3">
        {method.type === 'paypal' && paymentDetails.paypal_email && (
          <div className="space-y-2">
            <Label>PayPal Email</Label>
            <Input 
              value={paymentDetails.paypal_email} 
              readOnly 
              className="bg-gray-50 dark:bg-gray-800"
            />
          </div>
        )}
        
        {(method.type === 'bank_transfer_us' || method.type === 'bank_transfer_international') && (
          <>
            {paymentDetails.bank_name && (
              <div className="space-y-2">
                <Label>Bank Name</Label>
                <Input 
                  value={paymentDetails.bank_name} 
                  readOnly 
                  className="bg-gray-50 dark:bg-gray-800"
                />
              </div>
            )}
            {paymentDetails.account_number && (
              <div className="space-y-2">
                <Label>Account Number</Label>
                <Input 
                  value={paymentDetails.account_number} 
                  readOnly 
                  className="bg-gray-50 dark:bg-gray-800"
                />
              </div>
            )}
            {paymentDetails.routing_number && (
              <div className="space-y-2">
                <Label>Routing Number</Label>
                <Input 
                  value={paymentDetails.routing_number} 
                  readOnly 
                  className="bg-gray-50 dark:bg-gray-800"
                />
              </div>
            )}
            {method.type === 'bank_transfer_international' && paymentDetails.swift && (
              <div className="space-y-2">
                <Label>SWIFT Code</Label>
                <Input 
                  value={paymentDetails.swift} 
                  readOnly 
                  className="bg-gray-50 dark:bg-gray-800"
                />
              </div>
            )}
          </>
        )}
        
        {method.type === 'check' && paymentDetails.address && (
          <div className="space-y-2">
            <Label>Mailing Address</Label>
            <Textarea 
              value={paymentDetails.address} 
              readOnly 
              className="bg-gray-50 dark:bg-gray-800 min-h-[80px]"
            />
          </div>
        )}
      </div>
    );
  };

  return (
    <MainLayout allowedRoles={['author']}>
      <PageHeader
        title="Payment Preferences"
        description="View your payment methods and tax information"
      />
      
      <Tabs defaultValue="payment-methods" value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="payment-methods">Payment Methods</TabsTrigger>
          <TabsTrigger value="tax-info">Tax Information</TabsTrigger>
        </TabsList>
        
        <TabsContent value="payment-methods" className="space-y-4">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Read-Only View</AlertTitle>
            <AlertDescription>
              This is a read-only view of your payment preferences. To make changes, please contact your administrator.
            </AlertDescription>
          </Alert>

          <Card>
            <CardHeader>
              <CardTitle>Default Payment Method</CardTitle>
              <CardDescription>
                This is the payment method used for sending you royalty payments
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-20 w-full" />
                </div>
              ) : defaultMethod ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Payment Method Type</Label>
                    <Input 
                      value={formatPaymentMethod(defaultMethod.type)} 
                      readOnly 
                      className="bg-gray-50 dark:bg-gray-800"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Payment Threshold</Label>
                    <Input 
                      value={formatCurrency(defaultMethod.threshold)} 
                      readOnly 
                      className="bg-gray-50 dark:bg-gray-800"
                    />
                    <p className="text-xs text-muted-foreground">
                      Minimum balance required to request payment
                    </p>
                  </div>
                  
                  {renderPaymentMethodDetails(defaultMethod)}
                </div>
              ) : (
                <Alert>
                  <AlertTriangle className="h-4 w-4" />
                  <AlertTitle>No Default Payment Method</AlertTitle>
                  <AlertDescription>
                    You don't have a default payment method set up yet. Please contact your administrator to set one up.
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          </Card>
          
          {otherMethods.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Additional Payment Methods</CardTitle>
                <CardDescription>
                  Your backup payment options
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {otherMethods.map((method, index) => (
                    <div key={method.id} className="border rounded-lg p-4 space-y-4">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium">Payment Method {index + 1}</h4>
                        <span className="text-sm text-muted-foreground">Backup</span>
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Type</Label>
                        <Input 
                          value={formatPaymentMethod(method.type)} 
                          readOnly 
                          className="bg-gray-50 dark:bg-gray-800"
                        />
                      </div>
                      
                      <div className="space-y-2">
                        <Label>Threshold</Label>
                        <Input 
                          value={formatCurrency(method.threshold)} 
                          readOnly 
                          className="bg-gray-50 dark:bg-gray-800"
                        />
                      </div>
                      
                      {renderPaymentMethodDetails(method)}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
          
          {!isLoading && !defaultMethod && otherMethods.length === 0 && (
            <Card>
              <CardContent className="text-center py-12">
                <CreditCard className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                <h3 className="text-lg font-medium mb-2">No Payment Methods</h3>
                <p className="text-muted-foreground">
                  You don't have any payment methods set up yet. Please contact your administrator to add payment methods.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
        
        <TabsContent value="tax-info">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Read-Only View</AlertTitle>
            <AlertDescription>
              This is a read-only view of your tax information. To make changes, please contact your administrator.
            </AlertDescription>
          </Alert>

          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Tax Information</CardTitle>
              <CardDescription>
                Your tax settings and withholding information
              </CardDescription>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-3">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-10 w-full" />
                  <Skeleton className="h-4 w-48" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Tax Withholding Percentage</Label>
                    <Input 
                      value={`${authorData?.tax_percentage || 10}%`} 
                      readOnly 
                      className="bg-gray-50 dark:bg-gray-800"
                    />
                    <p className="text-xs text-muted-foreground">
                      Percentage of royalties withheld for tax purposes
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Tax Status</Label>
                    <Input 
                      value="Active" 
                      readOnly 
                      className="bg-gray-50 dark:bg-gray-800"
                    />
                  </div>
                  
                  {/* <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>Tax Documents</AlertTitle>
                    <AlertDescription>
                      Tax document management and 1099 forms will be handled by your administrator. You will receive copies of all relevant tax documents via email.
                    </AlertDescription>
                  </Alert> */}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </MainLayout>
  );
};

export default PaymentPreferences;
